// Mirrors trusstor_dashboard/shared/utils/sort.utils.ts `sortFloors`.
// Floors are bucketed by id shape (digits / digits-with-letter / 'pa' / special)
// and sorted within each bucket; storage floors are dropped.
//
// Order in the returned list (caller passes asc/desc):
//   1. floors whose id is purely digits (sorted numerically)
//   2. floors whose id ends in a letter (sorted by digits then letter)
//   3. PA floors
//   4. special floors

export type SortOrder = 'asc' | 'desc';

const FLOOR_PA = 'pa';
const FLOOR_STORAGE = 'storage';

type FloorLike = { floorId: string; isSpecial?: boolean; isSpecialFloor?: boolean };

function compareNumberStrings(a: string, b: string, order: SortOrder): number {
  return order === 'asc' ? Number(a) - Number(b) : Number(b) - Number(a);
}

function compareFloorIdsWithLetters(a: string, b: string, order: SortOrder): number {
  const letterA = a.charAt(a.length - 1);
  const letterB = b.charAt(b.length - 1);
  if (letterA === letterB) {
    return compareNumberStrings(a.substring(0, a.length - 1), b.substring(0, b.length - 1), order);
  }
  return order === 'asc' ? letterA.localeCompare(letterB) : letterB.localeCompare(letterA);
}

export function sortFloors<T extends FloorLike>(floors: T[], order: SortOrder): T[] {
  const filtered = floors.filter((f) => f.floorId !== FLOOR_STORAGE);
  const digits: T[] = [];
  const digitsWithLetters: T[] = [];
  const paFloors: T[] = [];
  const specialFloors: T[] = [];

  const digitsRegex = /^-?\d+$/;
  for (const floor of filtered) {
    if (floor.floorId === FLOOR_PA) {
      paFloors.push(floor);
    } else if (digitsRegex.test(floor.floorId)) {
      digits.push(floor);
    } else if (floor.isSpecial === true || floor.isSpecialFloor === true) {
      specialFloors.push(floor);
    } else {
      digitsWithLetters.push(floor);
    }
  }

  digits.sort((a, b) => compareNumberStrings(a.floorId, b.floorId, order));
  digitsWithLetters.sort((a, b) => compareFloorIdsWithLetters(a.floorId, b.floorId, order));
  paFloors.sort((a, b) => compareFloorIdsWithLetters(a.floorId, b.floorId, order));
  specialFloors.sort((a, b) => compareFloorIdsWithLetters(a.floorId, b.floorId, order));

  return [...digits, ...digitsWithLetters, ...paFloors, ...specialFloors];
}
